import { Schema } from '../data/dto/schema';
import { EntityExportDto } from '../data/dto/entity-options';
import { Dto } from '../data/dto/dto';
import { DTO_DEFAULT, DtoType } from '../data/dto/dto-types';
import { $log } from './logger';
import { Moment } from 'moment';
import * as moment from 'moment';
import { FORMAT } from './formats';
import { isArray, isBoolean, isDefined, isNullOrUndefined } from './object';
import { camelcase, isString } from './string';

/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-blacklist, id-match */

export interface OssEntityOptions {
    // Accepts an array of dtos, it uses the first one as base, if none is present it uses
    // the extension properties
    dto: any;
    extension?: any;
    mergeDtos?: any[];
    props?: any[];
}

export interface OssEntityToDtoMapping {
    [key: string]: {
        target;
        fn;
        entity?;
        isArray?;
        isSchemaArray?;
        propertySchema?;
    };
}

export interface OssDtoSchema {
    map?: string; // Custom property name
    mapImport?: Function; // custom property import mapping function
    mapExport?: Function; // custom property export mapping function
    default?: any;
    type?: any;
}

export interface OssDtoOptions {
    [key: string]: OssDtoSchema | Function | Function[];
}

export function getEntityToDtoMapping(
    instance: any,
    schema: any
): OssEntityToDtoMapping {
    let mapping: OssEntityToDtoMapping = {};

    Object.keys(schema).forEach((key) => {
        let classKey: string;
        let fn: Function = null;
        let include: boolean = true;
        let entity: Function;
        let isClassArray: boolean;
        let isSchemaArray: boolean;
        let propertySchema: Schema;

        if (schema[key]) {
            if (isArray(schema[key])) {
                if (typeof schema[key][0] === typeof Schema) {
                    isSchemaArray = true;
                    propertySchema = schema[key][0];
                } else {
                    isClassArray = true;
                    entity = schema[key][0];
                }

                classKey = camelcase(key);
            } else if (typeof schema[key] === 'function') {
                entity = schema[key];
                classKey = camelcase(key);
            } else if (schema[key].map || schema[key].mapExport) {
                classKey = schema[key].map || camelcase(key);

                if (schema[key].mapExport) {
                    fn = schema[key].mapExport;
                }
            } else if (
                schema[key] === Schema.String ||
                schema[key] === Schema.Number ||
                schema[key] === Schema.Boolean ||
                schema[key] === Schema.Moment ||
                schema[key] === Schema.Auto
            ) {
                propertySchema = schema[key];
                classKey = camelcase(key);
            } else {
                // Do not include in mapping
                classKey = key;
                include = false;
            }
        } else {
            classKey = camelcase(key);
        }

        mapping[classKey] = { target: include ? key : false, fn };

        if (entity) {
            mapping[classKey].entity = entity;

            if (isClassArray) {
                mapping[classKey].isArray = true;
            }
        }

        if (propertySchema) {
            mapping[classKey].propertySchema = propertySchema;

            if (isSchemaArray) {
                mapping[classKey].isSchemaArray = isSchemaArray;
            }
        }
    });

    return mapping;
}

export function DtoImport(
    options:
        | { [key: string]: OssEntityOptions }
        | {
              [key: string]:
                  | OssDtoSchema
                  | Function
                  | Function[]
                  | Schema
                  | Schema[];
          }
) {
    if (!options) {
        throw new Error('OssDto options not provided!');
    }

    return function _OssDto(
        target: any,
        propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        let processDtoMethod: Function = processDto;
        descriptor.value = function (...args: any[]) {
            let result = processDtoMethod.call(this, ...args, target, options);
            return result;
        };
    };
}

export const DTO_FORCE_CONSTRUCTOR = '__dtoForceConstructor';

export function DtoForceConstructor(constructor) {
    constructor.prototype[DTO_FORCE_CONSTRUCTOR] = true;
}

export function DtoExport(
    options:
        | { [key: string]: OssEntityOptions }
        | {
              [key: string]:
                  | OssDtoOptions
                  | Function
                  | Function[]
                  | Schema
                  | Schema[];
          }
        | EntityExportDto[]
) {
    if (!options) {
        throw new Error('OssEntity options not provided!');
    }

    return function _OssEntity(
        target: any,
        propertyKey: string,
        descriptor: PropertyDescriptor
    ) {
        let exportAsDtoMethod: Function = exportAsDto;
        descriptor.value = function (...args: any[]) {
            // Attempt to find default dto
            let defaultDto: Dto = null;

            if (isArray(options) && options.length) {
                defaultDto = (options as []).find(
                    (dtoOption: EntityExportDto) =>
                        dtoOption.type === DTO_DEFAULT
                );
            }

            let dtoSchema: any = defaultDto ? defaultDto.dto : options;

            if (args && args.length === 0) {
                args.push(DTO_DEFAULT);
                dtoSchema = (defaultDto && defaultDto.dto) || options;
            } else if (args[0]) {
                if (args[0] === DTO_DEFAULT) {
                    dtoSchema = (defaultDto && defaultDto.dto) || options;
                } else {
                    const entityExportOption: EntityExportDto = (options as [

                    ]).find(
                        (dtoOption: EntityExportDto) =>
                            dtoOption.type === args[0]
                    );

                    if (entityExportOption && entityExportOption.dto) {
                        dtoSchema =
                            entityExportOption && entityExportOption.dto;
                    } else {
                        dtoSchema = (defaultDto && defaultDto.dto) || options;
                    }
                }
            }

            let result = exportAsDtoMethod.call(this, ...args, dtoSchema);
            return result;
        };
    };
}

export function exportAsDto(type: DtoType = DTO_DEFAULT, options?: any) {
    let instance: any = this;
    let schema: OssEntityToDtoMapping = getEntityToDtoMapping(
        instance,
        options
    );

    let result: Dto = {};

    Object.keys(schema).forEach((key) => {
        const { target, fn, entity, isSchemaArray, propertySchema } = schema[
            key
        ];

        if (!isNullOrUndefined(instance[key])) {
            if (target) {
                if (entity) {
                    if (schema[key].isArray) {
                        result[target] = (instance[key] || []).map((value) => {
                            if (value.export) {
                                return value.export(type);
                            } else {
                                return value;
                            }
                        });
                    } else {
                        result[target] =
                            instance[key].export &&
                            typeof instance[key].export === 'function'
                                ? instance[key].export(type)
                                : instance[key];
                    }
                } else if (fn) {
                    result[target] = fn(instance[key]);
                } else if (propertySchema === Schema.Moment) {
                    if (isSchemaArray) {
                        if (instance[key] && isArray(instance[key])) {
                            result[target] = instance[key].map((item: Moment) =>
                                item.format(FORMAT.DATETIME_SERVER)
                            );
                        } else {
                            result[target] = instance[key];
                        }
                    } else {
                        result[target] = (instance[key] as Moment).format(
                            FORMAT.DATETIME_SERVER
                        );
                    }
                } else {
                    result[target] = instance[key];
                }
            }
        }
    });

    return result;
}

export function processDto(dto: any, parentClass: any, options?: any) {
    if (dto === null || !isDefined(dto)) {
        return dto;
    }
    let obj = {};

    Object.keys(options).forEach((key) => {
        let schema: any = options[key];
        let processedKey: string;
        let dtoValue: any = dto[key];
        let forceSchema: Schema;
        let isSchemaArray: boolean = false;

        if (isArray(schema)) {
            // get child class
            const klass: any = schema[0];
            if (dtoValue && dtoValue.length) {
                processedKey = camelcase(key);

                if (
                    dtoValue.length === 1 &&
                    (dtoValue[0] === Schema.String ||
                        dtoValue[0] === Schema.Number ||
                        dtoValue[0] === Schema.Boolean ||
                        dtoValue[0] === Schema.Moment ||
                        dtoValue[0] === Schema.Auto)
                ) {
                    if (dtoValue[0] !== Schema.Auto) {
                        isSchemaArray = true;
                        forceSchema = dtoValue[0];
                    }
                } else {
                    let result: any[] = [];
                    dtoValue
                        .filter((value) => isDefined(value))
                        .forEach((value) => {
                            result.push((klass as any).import(value));
                        });
                    dtoValue = result;
                }
            }
        } else if (typeof schema === 'function') {
            processedKey = camelcase(key);
            if (isDefined(dtoValue)) {
                dtoValue = schema.import(dtoValue);
            }
        } else {
            // Process as normal
            // Figure out property name
            if (
                schema === Schema.String ||
                schema === Schema.Number ||
                schema === Schema.Boolean ||
                schema === Schema.Moment ||
                schema === Schema.Auto
            ) {
                // camelcase
                processedKey = camelcase(key);
                if (schema !== Schema.Auto) {
                    forceSchema = schema;
                }
            } else {
                processedKey = schema.map ? schema.map : camelcase(key);
                // Custom Property
                if (schema.mapImport) {
                    dtoValue = schema.mapImport(dtoValue);
                } else if (isDefined(schema.default)) {
                    dtoValue = schema.default;
                }
            }
        }
        // assign value
        if (isDefined(dtoValue) && processedKey) {
            if (forceSchema) {
                if (isSchemaArray) {
                    if (dtoValue && isArray(dtoValue)) {
                        if (forceSchema === Schema.Number) {
                            obj[processedKey] = dtoValue.map(
                                ([item, index]: [any, number]) => {
                                    if (isNaN(item)) {
                                        // Attempt to convert to number or throw error
                                        const numberValue: number = Number(
                                            item
                                        );

                                        if (isNaN(numberValue)) {
                                            $log.error(`Dto Import Error: Property ${key} in object of class ${parentClass.prototype.name}
                                            could not be converted to number array. Faulty value: ${item} at index ${index}`);
                                        } else {
                                            return numberValue;
                                        }
                                    } else {
                                        // it is a number
                                        return Number(item);
                                    }
                                }
                            );
                        } else if (forceSchema === Schema.String) {
                            obj[processedKey] = dtoValue.map((item: any) => {
                                if (isString(item)) {
                                    return item;
                                } else {
                                    return item.toString();
                                }
                            });
                        } else if (forceSchema === Schema.Boolean) {
                            obj[processedKey] = dtoValue.map((item: any) => {
                                if (isBoolean(item)) {
                                    return item;
                                } else {
                                    // Cast to boolean
                                    return getBoolean(item);
                                }
                            });
                        } else if (forceSchema === Schema.Moment) {
                            obj[processedKey] = dtoValue.map(
                                ([item, index]: [any, number]) => {
                                    // attemp to convert to moment
                                    const itemMoment: Moment = moment(
                                        item,
                                        FORMAT.DATETIME_SERVER
                                    );

                                    if (itemMoment.isValid()) {
                                        return itemMoment;
                                    } else {
                                        $log.error(`Dto Import Error: Property ${key} in object of class ${parentClass.prototype.name}
                                            could not be converted to Moment Object. Faulty value: ${item} at index ${index}. Please verify
                                            the value is a string with format '${FORMAT.DATETIME_SERVER}'`);
                                    }
                                }
                            );
                        }
                    } else {
                        obj[processedKey] = dtoValue;
                    }
                } else {
                    if (forceSchema === Schema.Number) {
                        if (isNaN(dtoValue)) {
                            // Attempt to convert to number or throw error
                            const numberValue: number = Number(dtoValue);

                            if (isNaN(numberValue)) {
                                $log.error(
                                    `Dto Import Error: Property ${key} in object of class ${parentClass.prototype.name} could not be converted to number`
                                );
                            } else {
                                obj[processedKey] = numberValue;
                            }
                        } else {
                            // it is a number
                            obj[processedKey] = Number(dtoValue);
                        }
                    } else if (forceSchema === Schema.String) {
                        if (isString(dtoValue)) {
                            obj[processedKey] = dtoValue;
                        } else {
                            obj[processedKey] = dtoValue?.toString();
                        }
                    } else if (forceSchema === Schema.Boolean) {
                        if (isBoolean(dtoValue)) {
                            obj[processedKey] = dtoValue;
                        } else {
                            // Cast to boolean
                            obj[processedKey] = getBoolean(dtoValue);
                        }
                    } else if (forceSchema === Schema.Moment) {
                        const dtoValueMoment: Moment = moment(
                            dtoValue,
                            FORMAT.DATETIME_SERVER
                        );

                        if (dtoValue) {
                            if (dtoValueMoment.isValid()) {
                                obj[processedKey] = dtoValueMoment;
                            } else {
                                $log.error(`Dto Import Error: Property ${key} in object of class ${parentClass.prototype.name}
                                                could not be converted to Moment Object. Faulty value: ${dtoValue}. Please verify
                                                the value is a string with format '${FORMAT.DATETIME_SERVER}'`);
                            }
                        } else {
                            obj[processedKey] = dtoValue;
                        }
                    }
                }
            } else {
                obj[processedKey] = dtoValue;
            }
        }
    });

    if (parentClass.prototype && parentClass.prototype[DTO_FORCE_CONSTRUCTOR]) {
        obj = new parentClass(obj);
    } else {
        obj = Object.assign(new parentClass(), obj);
    }

    return obj;
}

function getBoolean(value) {
    switch (value) {
        case true:
        case 'true':
        case 1:
        case '1':
        case 'on':
        case 'yes':
            return true;
        default:
            return false;
    }
}
